import { Box, Flex, Icon, Text, HStack, Badge } from '@chakra-ui/react';
import { IconType } from 'react-icons';
import {
    TbFileDescription,
    TbShoppingCart,
    TbAlertTriangle,
    TbCheck,
    TbRefresh,
    TbClock,
    TbAt,
    TbMessage,
} from 'react-icons/tb';
import { NotificationItem, NotificationType } from '../../hooks/useGetNotifications';
import { SOURCE_MARKETPLACE_CONFIG } from '../SourceBadge';

interface IconConfig {
    icon: IconType;
    color: string;
    bg: string;
}

export const getNotificationIcon = (type: NotificationType): IconConfig => {
    const iconMap: Record<NotificationType, IconConfig> = {
        'new-order': { icon: TbShoppingCart, color: 'blue.500', bg: 'blue.50' },
        'quotes-extended': { icon: TbClock, color: 'purple.500', bg: 'purple.50' },
        'auto-requote': { icon: TbRefresh, color: 'cyan.500', bg: 'cyan.50' },
        'quotes-back': { icon: TbCheck, color: 'green.500', bg: 'green.50' },
        'quotes-back-potential-total': { icon: TbCheck, color: 'green.500', bg: 'green.50' },
        'quotes-back-quotes-only': { icon: TbCheck, color: 'green.500', bg: 'green.50' },
        'no-quoted-parts': { icon: TbAlertTriangle, color: 'yellow.600', bg: 'yellow.50' },
        'flagged-parts': { icon: TbAlertTriangle, color: 'orange.500', bg: 'orange.50' },
        'orders-confirmed': { icon: TbCheck, color: 'green.500', bg: 'green.50' },
        'orders-rejected': { icon: TbAlertTriangle, color: 'red.500', bg: 'red.50' },
        'orders-returned-cancelled': { icon: TbRefresh, color: 'orange.500', bg: 'orange.50' },
        'rekeying-completed': { icon: TbCheck, color: 'green.500', bg: 'green.50' },
        'rekeying-completed-flagged': { icon: TbAlertTriangle, color: 'yellow.600', bg: 'yellow.50' },
        'rekeying-skipped': { icon: TbClock, color: 'gray.500', bg: 'gray.100' },
        'tesla-order-error': { icon: TbAlertTriangle, color: 'red.500', bg: 'red.50' },
        'part-number-changed': { icon: TbRefresh, color: 'blue.500', bg: 'blue.50' },
        'pre-order-backorder': { icon: TbAlertTriangle, color: 'red.500', bg: 'red.50' },
        'back-order-ordered': { icon: TbShoppingCart, color: 'orange.500', bg: 'orange.50' },
        'no-oem-quotes': { icon: TbAlertTriangle, color: 'red.500', bg: 'red.50' },
        'order-mention': { icon: TbAt, color: 'blue.500', bg: 'blue.50' },
        'invoice-mention': { icon: TbAt, color: 'blue.500', bg: 'blue.50' },
        'vendor-message': { icon: TbAt, color: 'blue.500', bg: 'blue.50' },
        'supplier-changes': { icon: TbAlertTriangle, color: 'orange.500', bg: 'orange.50' },
        'needs-rekey': { icon: TbAlertTriangle, color: 'yellow.600', bg: 'yellow.50' },
        'rekey-advisor-score': { icon: TbAlertTriangle, color: 'red.500', bg: 'red.50' },
        'order-not-confirmed': { icon: TbClock, color: 'orange.500', bg: 'orange.50' },
        'order-request-enabled': { icon: TbFileDescription, color: 'teal.500', bg: 'teal.50' },
        'flagged-invoices': { icon: TbAlertTriangle, color: 'orange.500', bg: 'orange.50' },
        'negotiation-handoff': { icon: TbMessage, color: 'blue.500', bg: 'blue.50' },
        'order-placement-failed': { icon: TbAlertTriangle, color: 'red.500', bg: 'red.50' },
        'ccc-manual-po-created': { icon: TbCheck, color: 'green.500', bg: 'green.50' },
        'ccc-manual-po-failed': { icon: TbAlertTriangle, color: 'red.500', bg: 'red.50' },
        'rekey-quote-only-completed': { icon: TbCheck, color: 'green.500', bg: 'green.50' },
        'inventory-stock-request': { icon: TbShoppingCart, color: 'blue.500', bg: 'blue.50' },
        'inventory-stock-request-placed': { icon: TbCheck, color: 'green.500', bg: 'green.50' },
    };
    return iconMap[type] ?? { icon: TbFileDescription, color: 'gray.500', bg: 'gray.100' };
};

export const getNotificationMessage = (type: NotificationType): string => {
    const messages: Record<NotificationType, string> = {
        'new-order': 'New order received',
        'quotes-extended': 'Quote deadline extended',
        'auto-requote': 'Parts automatically requoted',
        'quotes-back': 'Quotes are ready for review',
        'quotes-back-potential-total': 'Quotes are ready on a potential total order',
        'quotes-back-quotes-only': 'Quotes are ready on a quotes-only order',
        'no-quoted-parts': 'Some parts received no quotes',
        'flagged-parts': 'Parts flagged for review',
        'orders-confirmed': 'Orders confirmed by supplier',
        'orders-rejected': 'Order rejected by supplier',
        'orders-returned-cancelled': 'Parts returned or cancelled',
        'rekeying-completed': 'Re-keying completed',
        'rekeying-completed-flagged': 'Re-keying completed with flags',
        'rekeying-skipped': 'Re-keying was skipped',
        'tesla-order-error': 'Error placing Tesla order',
        'part-number-changed': 'Part number was corrected',
        'pre-order-backorder': 'Pre-order parts are backordered',
        'back-order-ordered': 'Backordered part was ordered',
        'no-oem-quotes': 'No OEM quotes received on re-quote',
        'order-mention': 'New message on an order',
        'invoice-mention': 'You were mentioned in an invoice comment',
        'vendor-message': 'New vendor message on an order',
        'supplier-changes': 'Supplier made changes to an order',
        'needs-rekey': 'Re-keying is needed on this order',
        'rekey-advisor-score': 'Advisor score 11 found during re-key',
        'order-not-confirmed': 'Order not confirmed by vendor',
        'order-request-enabled': 'Order request enabled',
        'flagged-invoices': 'Invoice flagged for review',
        'negotiation-handoff': 'Negotiation needs reply',
        'order-placement-failed': 'Order placement failed',
        'ccc-manual-po-created': 'CCC manual PO created',
        'ccc-manual-po-failed': 'CCC manual PO creation failed',
        'rekey-quote-only-completed': 'Re-keying completed on quote-only order',
        'inventory-stock-request': 'Inventory purchase order needs confirmation',
        'inventory-stock-request-placed': 'Internal inventory part was ordered',
    };
    return messages[type] ?? 'Notification';
};

export const getNotificationTag = (type: NotificationType): { label: string; color: string; bg: string } => {
    const tags: Record<NotificationType, { label: string; color: string; bg: string }> = {
        'new-order': { label: 'New Order', color: 'blue.700', bg: 'blue.100' },
        'quotes-extended': { label: 'Quoting', color: 'purple.700', bg: 'purple.100' },
        'auto-requote': { label: 'Quoting', color: 'cyan.700', bg: 'cyan.100' },
        'quotes-back': { label: 'Selection', color: 'green.700', bg: 'green.100' },
        'quotes-back-potential-total': { label: 'Potential Total', color: 'green.700', bg: 'green.100' },
        'quotes-back-quotes-only': { label: 'Quotes Only', color: 'green.700', bg: 'green.100' },
        'no-quoted-parts': { label: 'Selection', color: 'yellow.700', bg: 'yellow.100' },
        'flagged-parts': { label: 'Flagged', color: 'orange.700', bg: 'orange.100' },
        'orders-confirmed': { label: 'Ordered', color: 'green.700', bg: 'green.100' },
        'orders-rejected': { label: 'Rejected', color: 'red.700', bg: 'red.100' },
        'orders-returned-cancelled': { label: 'Return/Cancel', color: 'orange.700', bg: 'orange.100' },
        'rekeying-completed': { label: 'Re-key', color: 'green.700', bg: 'green.100' },
        'rekeying-completed-flagged': { label: 'Re-key', color: 'yellow.700', bg: 'yellow.100' },
        'rekeying-skipped': { label: 'Re-key', color: 'gray.600', bg: 'gray.100' },
        'tesla-order-error': { label: 'Tesla Error', color: 'red.700', bg: 'red.100' },
        'part-number-changed': { label: 'Part #', color: 'blue.700', bg: 'blue.100' },
        'pre-order-backorder': { label: 'Pre-order', color: 'red.700', bg: 'red.100' },
        'back-order-ordered': { label: 'Backorder', color: 'orange.700', bg: 'orange.100' },
        'no-oem-quotes': { label: 'No OEM', color: 'red.700', bg: 'red.100' },
        'order-mention': { label: 'Mention', color: 'blue.700', bg: 'blue.100' },
        'invoice-mention': { label: 'Invoice', color: 'blue.700', bg: 'blue.100' },
        'vendor-message': { label: 'Vendor', color: 'blue.700', bg: 'blue.100' },
        'supplier-changes': { label: 'Supplier', color: 'orange.700', bg: 'orange.100' },
        'needs-rekey': { label: 'Re-key', color: 'yellow.700', bg: 'yellow.100' },
        'rekey-advisor-score': { label: 'Advisor', color: 'red.700', bg: 'red.100' },
        'order-not-confirmed': { label: 'Not Confirmed', color: 'orange.700', bg: 'orange.100' },
        'order-request-enabled': { label: 'Order Request', color: 'teal.700', bg: 'teal.100' },
        'flagged-invoices': { label: 'Invoice', color: 'orange.700', bg: 'orange.100' },
        'negotiation-handoff': { label: 'Negotiation', color: 'blue.700', bg: 'blue.100' },
        'order-placement-failed': { label: 'Order Failed', color: 'red.700', bg: 'red.100' },
        'ccc-manual-po-created': { label: 'CCC PO', color: 'green.700', bg: 'green.100' },
        'ccc-manual-po-failed': { label: 'CCC PO Failed', color: 'red.700', bg: 'red.100' },
        'rekey-quote-only-completed': { label: 'Re-key', color: 'green.700', bg: 'green.100' },
        'inventory-stock-request': { label: 'Purchase Order', color: 'blue.700', bg: 'blue.100' },
        'inventory-stock-request-placed': { label: 'In Stock Ordered', color: 'green.700', bg: 'green.100' },
    };
    return tags[type] ?? { label: 'Other', color: 'gray.600', bg: 'gray.100' };
};

/**
 * Message for a specific notification item — layers per-item overrides
 * (marketplace source on order-placement-failed) on top of the per-type copy.
 * Used by the bell-drawer row and the order page audit-trail surfaces.
 */
export const getNotificationItemMessage = (notification: NotificationItem): string => {
    if (
        notification.type === 'order-placement-failed' &&
        notification.data.type === 'order' &&
        notification.data.source
    ) {
        const sourceLabel = SOURCE_MARKETPLACE_CONFIG[notification.data.source]?.label;
        if (sourceLabel) {
            return `${sourceLabel} order placement failed`;
        }
    }
    return getNotificationMessage(notification.type);
};

const formatRelativeTime = (date: Date): string => {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffMs = now.getTime() - notificationDate.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return notificationDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
    });
};

interface NotificationRowProps {
    notification: NotificationItem;
    onClick: () => void;
}

const NotificationRow = ({ notification, onClick }: NotificationRowProps) => {
    const { icon, color, bg } = getNotificationIcon(notification.type);
    let tag = getNotificationTag(notification.type);
    let message = getNotificationItemMessage(notification);

    // order-mention covers both explicit @mentions and messages left on orders the user quoted
    if (notification.type === 'order-mention') {
        const sender = notification.senderName ?? 'Someone';
        if (notification.explicit === true) {
            message = `${sender} mentioned you in a message`;
        } else {
            message = `${sender} left a message`;
            tag = { label: 'Message', color: 'blue.700', bg: 'blue.100' };
        }
    }

    if (notification.type === 'invoice-mention') {
        const sender = notification.senderName ?? 'Someone';
        message = `${sender} mentioned you in an invoice comment`;
    }

    return (
        <Box
            px={4}
            py={3}
            cursor="pointer"
            bg={notification.read ? 'white' : 'blue.50'}
            borderBottomWidth="1px"
            borderColor="gray.100"
            _hover={{ bg: notification.read ? 'gray.50' : 'blue.100' }}
            onClick={onClick}
            transition="background-color 150ms ease"
        >
            <Flex gap={3} align="start">
                <Flex
                    w="36px"
                    h="36px"
                    borderRadius="full"
                    bg={bg}
                    align="center"
                    justify="center"
                    flexShrink={0}
                >
                    <Icon as={icon as React.ElementType} w={4} h={4} color={color} />
                </Flex>

                <Box flex={1} minW={0}>
                    <HStack spacing={2} align="center">
                        <Text
                            fontWeight={notification.read ? 'normal' : 'semibold'}
                            fontSize="sm"
                            color="gray.800"
                        >
                            {message}
                        </Text>
                        <Badge
                            fontSize="9px"
                            px={1.5}
                            py={0.5}
                            borderRadius="md"
                            bg={tag.bg}
                            color={tag.color}
                            fontWeight="medium"
                            textTransform="none"
                        >
                            {tag.label}
                        </Badge>
                    </HStack>
                    {notification.preview && (
                        <Text fontSize="xs" color="gray.500" mt={0.5} noOfLines={1}>
                            {notification.preview.vehicle}
                            {notification.preview.roNumber && ` • RO ${notification.preview.roNumber}`}
                        </Text>
                    )}
                    {notification.type === 'tesla-order-error' && (
                        <Text fontSize="xs" color="red.500" mt={0.5} fontStyle="italic">
                            Verify RO exists in Tesla EPC
                        </Text>
                    )}
                    {notification.type === 'order-placement-failed' && (
                        <Text fontSize="xs" color="red.500" mt={0.5} fontStyle="italic">
                            Selected parts are no longer available
                        </Text>
                    )}
                    {notification.type === 'orders-returned-cancelled' && notification.senderName && (
                        <Text fontSize="xs" color="orange.600" mt={0.5} fontWeight="medium">
                            {notification.senderName}
                        </Text>
                    )}
                    {/* Supplier-side rejections carry the supplier name so the row
                        reads like the email: "Supplier X rejected an order". */}
                    {notification.type === 'orders-rejected' && notification.senderName && (
                        <Text fontSize="xs" color="red.600" mt={0.5} fontWeight="medium">
                            {notification.senderName} rejected an order
                        </Text>
                    )}
                    <Text fontSize="xs" color="gray.400" mt={1}>
                        {formatRelativeTime(notification.timeCreated)}
                    </Text>
                </Box>

                {!notification.read && (
                    <HStack spacing={0} flexShrink={0} align="center" h="36px">
                        <Box w="8px" h="8px" borderRadius="full" bg="blue.500" />
                    </HStack>
                )}
            </Flex>
        </Box>
    );
};

export default NotificationRow;
